import React from 'react';
import {
  PiArrowSquareOut,
  PiDeviceMobileSpeaker,
  PiDownloadSimple,
  PiEnvelopeSimple,
  PiFacebookLogo,
  PiGlobe,
  PiInstagramLogo,
  PiPhone,
  PiWhatsappLogo,
} from 'react-icons/pi';
import Logo from '../components/Logo';
import { CONNECT_PROFILE, CONNECT_WORK } from '../config/connect';

const ConnectDecor = () => (
  <div className="connect-decor" aria-hidden="true">
    <span className="connect-decor-ring connect-decor-ring-a" />
    <span className="connect-decor-ring connect-decor-ring-b" />
    <span className="connect-decor-triangle connect-decor-triangle-a" />
    <span className="connect-decor-triangle connect-decor-triangle-b" />
    <span className="connect-decor-square connect-decor-square-a" />
    <span className="connect-decor-square connect-decor-square-b" />
    <span className="connect-decor-bar connect-decor-bar-a" />
    <span className="connect-decor-bar connect-decor-bar-b" />
    <span className="connect-decor-bar connect-decor-bar-c" />
  </div>
);

const Connect = () => (
  <div className="connect-page relative overflow-x-hidden bg-white text-ink">
    <section className="relative isolate overflow-hidden bg-[#031a3d] px-5 pb-12 pt-10 text-white sm:px-8 sm:pb-14 sm:pt-12">
      <ConnectDecor />
      <div className="relative z-10 mx-auto max-w-lg">
        <a href="/" className="inline-flex rounded-xl bg-white px-3 py-2.5 shadow-sm" aria-label="Lokyis Ltd home">
          <Logo heightClass="h-8 sm:h-9" />
        </a>

        <div className="mt-10">
          <div className="connect-accent-bar" aria-hidden="true" />
          <h1 className="mt-5 text-[2.5rem] font-semibold leading-none tracking-[-0.05em] sm:text-5xl">
            {CONNECT_PROFILE.name}
          </h1>
          <p className="mt-3 text-base font-medium text-white/75">
            {CONNECT_PROFILE.title} · {CONNECT_PROFILE.company}
          </p>
          <p className="mt-5 max-w-sm text-lg leading-7 text-white/90">
            {CONNECT_PROFILE.intro}
          </p>
        </div>

        <div className="mt-9 grid gap-3">
          <a
            href={CONNECT_PROFILE.vcardHref}
            download={CONNECT_PROFILE.vcardFilename}
            className="connect-action connect-action-primary"
          >
            <PiDownloadSimple aria-hidden="true" className="text-xl" />
            Save contact
          </a>
          <a
            href={`mailto:${CONNECT_PROFILE.email}`}
            className="connect-action connect-action-secondary"
          >
            <PiEnvelopeSimple aria-hidden="true" className="text-xl" />
            Email Tom
          </a>
          <a
            href={CONNECT_PROFILE.phoneHref}
            className="connect-action connect-action-secondary"
          >
            <PiPhone aria-hidden="true" className="text-xl" />
            Call Tom
          </a>
          <a
            href={CONNECT_PROFILE.whatsappHref}
            target="_blank"
            rel="noreferrer"
            className="connect-action connect-action-secondary"
          >
            <PiWhatsappLogo aria-hidden="true" className="text-xl" />
            WhatsApp Tom
          </a>
        </div>

        <p className="mt-5 text-sm text-white/55">
          <a href={CONNECT_PROFILE.phoneHref} className="underline-offset-2 hover:text-white hover:underline">
            {CONNECT_PROFILE.phoneDisplay}
          </a>
          {' · '}
          <a href={`mailto:${CONNECT_PROFILE.email}`} className="underline-offset-2 hover:text-white hover:underline">
            {CONNECT_PROFILE.email}
          </a>
        </p>
      </div>
    </section>

    <section className="relative px-5 py-12 sm:px-8 sm:py-14" aria-labelledby="connect-work-heading">
      <div className="mx-auto max-w-lg">
        <p className="text-sm font-bold uppercase tracking-[0.16em] text-violet-dark">Selected work</p>
        <div className="connect-accent-bar mt-4" aria-hidden="true" />
        <h2 id="connect-work-heading" className="mt-5 text-3xl font-semibold tracking-[-0.04em]">
          Projects you can look at now
        </h2>
        <p className="mt-3 text-base leading-7 text-ink/65">
          A mix of client website work and Lokyis products — labelled so you can tell which is which.
        </p>

        <ul className="mt-8 space-y-5">
          {CONNECT_WORK.map((item) => (
            <li key={item.id}>
              <a
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className="connect-work-card group"
              >
                <div className="connect-work-media">
                  <img src={item.image} alt={item.imageAlt} loading="lazy" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-violet-dark">
                    {item.kind}
                  </p>
                  <h3 className="mt-2 text-xl font-semibold tracking-[-0.03em] group-hover:text-magenta">
                    {item.name}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-ink/65">{item.summary}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-ink">
                    View
                    <PiArrowSquareOut aria-hidden="true" />
                  </span>
                </div>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section className="border-t border-ink/10 bg-[#031a3d] px-5 py-12 text-white sm:px-8 sm:py-14">
      <div className="relative mx-auto max-w-lg">
        <span className="connect-decor-bar connect-decor-bar-d" aria-hidden="true" />
        <h2 className="text-3xl font-semibold leading-tight tracking-[-0.04em]">
          {CONNECT_PROFILE.enquiry}
        </h2>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <a href={`mailto:${CONNECT_PROFILE.email}`} className="connect-action connect-action-primary">
            <PiEnvelopeSimple aria-hidden="true" className="text-xl" />
            Email Tom
          </a>
          <a href={CONNECT_PROFILE.phoneHref} className="connect-action connect-action-secondary">
            <PiDeviceMobileSpeaker aria-hidden="true" className="text-xl" />
            Call Tom
          </a>
        </div>
      </div>
    </section>

    <section className="px-5 py-10 sm:px-8" aria-label="More ways to find Lokyis">
      <div className="mx-auto flex max-w-lg flex-col gap-4">
        <a href={CONNECT_PROFILE.website} className="connect-link-row">
          <PiGlobe aria-hidden="true" className="text-xl text-violet-dark" />
          <span>
            <span className="block text-sm font-semibold">Website</span>
            <span className="text-sm text-ink/55">lokyis.com</span>
          </span>
        </a>
        <a href={CONNECT_PROFILE.instagram} target="_blank" rel="noreferrer" className="connect-link-row">
          <PiInstagramLogo aria-hidden="true" className="text-xl text-magenta" />
          <span>
            <span className="block text-sm font-semibold">Instagram</span>
            <span className="text-sm text-ink/55">@lokyis_official</span>
          </span>
        </a>
        <a href={CONNECT_PROFILE.facebook} target="_blank" rel="noreferrer" className="connect-link-row">
          <PiFacebookLogo aria-hidden="true" className="text-xl text-violet-dark" />
          <span>
            <span className="block text-sm font-semibold">Facebook</span>
            <span className="text-sm text-ink/55">Lokyis Ltd</span>
          </span>
        </a>
      </div>
    </section>
  </div>
);

export default Connect;
